import { useMutation, useQuery } from "@apollo/client";
import { Checkbox, Col, Divider, Form, Input, Row, Select, Tooltip, Upload, message } from "antd";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { LuCircleHelp, LuPlus } from "react-icons/lu";
import { isEmpty, uniqBy } from "lodash";
import PhoneInput from "react-phone-number-input";
import { DEFAULT_COUNTRY } from "../../../config";
import { CREATE_LOCATION, UPDATE_LOCATION } from "../../../graphql/mutation";
import {
  GET_OPPORTUNITTIES_TYPES,
  GET_SUPPORTED_LANGUAGES_BY_LOCATION,
} from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import TextInput from "../../../library/textInput";
import { Countries } from "../../../utils/countries";
import { USStates } from "../../../utils/usStates";
import CustomButton from "../../commonComponents/button";
import LanguageSelect from "../../commonComponents/LanguageSelect";
import TimezoneSelect from "../../commonComponents/TimezoneSelect";
import { LANGUAGES } from "../../../utils/languages";
import {
  MFA_METHODS,
  LOCATION_TYPES,
  LOCATION_S3_FOLDER,
  MAX_LOCATION_LOGO_SIZE_MB,
} from "../../../constant/LocationConstant";
import { s3uploader } from "../../../utils/awsS3MediaUploader";
import { getLocationPayload } from "../../../helpers/LocationHelpers";
import SelectOpportunityStatus from "../../commonComponents/SelectOpportunityStatus";

// --- Helper Components ---
const UploadLogo = React.memo(function UploadLogo({ logoList, onChange, onRemove, uploadButton }) {
  const beforeUpload = file => {
    const isLtMaxSize = file.size / 1024 / 1024 < MAX_LOCATION_LOGO_SIZE_MB;
    if (!isLtMaxSize) {
      message.error(`Image must be smaller than ${MAX_LOCATION_LOGO_SIZE_MB}MB!`);
    }
    return false; // Prevent auto upload
  };

  return (
    <div className="bg-fill rounded-full inline-block">
      <Upload
        name="logo"
        listType="picture-circle"
        accept="image/*"
        fileList={logoList}
        beforeUpload={beforeUpload}
        onChange={onChange}
        onRemove={onRemove}
        maxCount={1}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
          showDownloadIcon: false,
        }}
      >
        {logoList.length < 1 && uploadButton}
      </Upload>
    </div>
  );
});

const FormLabel = ({ children }) => (
  <span className="text-default text-[14px] font-normal">{children}</span>
);

// --- Main Component ---
const LocationForm = ({
  editableLocation = null,
  regions,
  setEditableLocation = () => {},
  refetch = () => {},
  companyId,
  onClose,
  isSubmit,
  setIsSubmit = () => {},
  setEditMode = () => {},
  setEditModeData = () => {},
  handleLocationView = () => {},
  form,
}) => {
  const [lang] = useLang("locationPage");
  const vdpTextRef = useRef(null);

  // State
  const [vdpLink, setvdpLink] = useState("");
  const [error, setError] = useState("");
  const [location, setLocation] = useState(null);
  const [autoResponseEnabled, setAutoResponseEnabled] = useState(false);
  const [logoList, setLogoList] = useState([]);
  const [opportunityTypes, setOpportunityTypes] = useState([]);

  // Mutations
  const [createLocation] = useMutation(CREATE_LOCATION);
  const [updateLocation] = useMutation(UPDATE_LOCATION);

  // Query - Fetch supported languages for edit mode
  const { data: supportedLanguagesData } = useQuery(GET_SUPPORTED_LANGUAGES_BY_LOCATION, {
    variables: {
      locationId: editableLocation?.id,
    },
    skip: !editableLocation?.id,
  });

  // Fetch opportunity types
  const { loading: opportunityTypesLoading } = useQuery(GET_OPPORTUNITTIES_TYPES, {
    variables: { companyId: companyId },
    fetchPolicy: "cache-and-network",
    onCompleted: res => {
      const types = res?.getOpportunityTypes || [];
      const uniqueTypes = uniqBy(types, "id");
      // Keep original data structure for tree display
      setOpportunityTypes(uniqueTypes);
      // Set form field value so Form.Item can pass it to the component
      form.setFieldsValue({ opportunityType: uniqueTypes[0]?.id });
    },
  });

  // Transform supported languages data into format compatible with LanguageSelect
  const supportedLanguages = React.useMemo(() => {
    if (!supportedLanguagesData?.getSupportedLanguages) return [];

    const languages = supportedLanguagesData.getSupportedLanguages
      ?.filter(lang => lang.isActive)
      ?.map(lang => ({
        code: lang.languageCode,
        name: lang.languageName,
        label: lang.languageName,
        value: lang.languageCode,
      }));

    // If preferred language is "en" and not in supported languages, add it
    const hasEnglish = languages.some(lang => lang.value === "en");
    const preferredLang =
      location?.preferredLanguage || editableLocation?.settings?.PREFERRED_LANGUAGE;

    if (preferredLang === "en" && !hasEnglish) {
      const englishLang = LANGUAGES.find(lang => lang.value === "en");
      if (englishLang) {
        languages.unshift(englishLang);
      }
    }

    return languages;
  }, [
    supportedLanguagesData,
    location?.preferredLanguage,
    editableLocation?.settings?.PREFERRED_LANGUAGE,
  ]);

  // Handlers
  const updateLocationForm = useCallback(
    (field, value) => {
      setLocation(prev => {
        const updated = { ...prev, [field]: value };
        // Also update the form field value to keep AntD Form in sync
        form.setFieldsValue({ [field]: value });
        return updated;
      });
    },
    [form]
  );

  const validateForm = useCallback((location, setError) => {
    if (!location?.title) {
      setError("Location name is required");
      return false;
    }
    if (!location?.displayName) {
      setError("Display name is required");
      return false;
    }
    if (!location?.regionId) {
      setError("Region is required");
      return false;
    }
    if (!location?.locationType) {
      setError("Location type is required");
      return false;
    }
    if (!location?.timezone) {
      setError("Timezone is required");
      return false;
    }
    setError("");
    return true;
  }, []);

  // Define handleUpdateClick before handleUpdate and useEffect
  const handleUpdateClick = useCallback(
    async values => {
      // Always get latest form values to ensure all fields are included
      const allValues = form.getFieldsValue(true);
      const mergedLocation = { ...location, ...allValues };

      // Convert latitude/longitude to float if present
      if (mergedLocation.latitude !== undefined && mergedLocation.latitude !== "") {
        mergedLocation.latitude = parseFloat(mergedLocation.latitude);
      }
      if (mergedLocation.longitude !== undefined && mergedLocation.longitude !== "") {
        mergedLocation.longitude = parseFloat(mergedLocation.longitude);
      }

      if (!validateForm(mergedLocation, setError)) {
        setIsSubmit(false);
        return;
      }
      setError("");

      // Upload logo if present
      let logoUrl = "";
      const logoFile = logoList?.[0]?.originFileObj;
      if (!isEmpty(logoFile)) {
        try {
          const uploadResult = await s3uploader(
            logoFile,
            `${process.env.GATSBY_S3_BUCKET}${LOCATION_S3_FOLDER}`,
            () => {},
            mergedLocation?.id || "",
            null,
            null,
            null
          );
          logoUrl = uploadResult?.url || "";
        } catch (err) {
          setIsSubmit(false);
        }
      } else if (logoList?.[0]?.url) {
        // If already uploaded (edit mode), use existing url
        logoUrl = logoList[0].url;
      }
      try {
        const variables = {
          ...mergedLocation,
          inventoryUrlFormat: vdpLink,
          autoResponseEnabled,
          isMfaEnabled: mergedLocation?.enableMFA || false,
          mfaMethods: mergedLocation?.mfaMethods || [],
          ...(logoUrl ? { locationLogo: logoUrl } : {}),
          ...(mergedLocation.latitude !== undefined ? { latitude: mergedLocation.latitude } : {}),
          ...(mergedLocation.longitude !== undefined
            ? { longitude: mergedLocation.longitude }
            : {}),
          displayName: mergedLocation.displayName,
          preferredLanguage: mergedLocation.preferredLanguage || "",
          isPushToCrm: mergedLocation.isPushToCRM || false,
          isPushToDms: mergedLocation.isPushToDMS || false,
          opportunityTypeId: mergedLocation.opportunityTypeId || null,
        };
        if (editableLocation) {
          variables.locationId = editableLocation.id;
          const apiPayload = getLocationPayload(variables);
          const resp = await updateLocation({ variables: apiPayload });
          if (resp?.data?.updateCompanyLocation?.statusCode === 200) {
            onClose && onClose();
            refetch && refetch();
            setEditableLocation && setEditableLocation(null);
            setEditMode(false);
            setEditModeData(null);
            message.success(resp?.data?.updateCompanyLocation?.message);
          } else {
            message.error(resp?.data?.updateCompanyLocation?.message);
          }
        } else {
          variables.companyId = companyId;
          variables.isDefault = false;
          variables.isActive = false;
          const apiPayload = getLocationPayload(variables, true); // Pass true to indicate create mode
          const resp = await createLocation({ variables: apiPayload });
          if (resp?.data?.createCompanyLocation?.statusCode === 200) {
            onClose && onClose();
            refetch && refetch();
            setEditableLocation && setEditableLocation(null);
            message.success(resp?.data?.createCompanyLocation?.message);
            handleLocationView(resp?.data?.createCompanyLocation?.location); // enable location detail view
          } else {
            message.error(resp?.data?.createCompanyLocation?.message);
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Error in create/update Location", err);
      } finally {
        setIsSubmit(false);
      }
    },
    [
      autoResponseEnabled,
      companyId,
      createLocation,
      editableLocation,
      location,
      logoList,
      onClose,
      refetch,
      setEditMode,
      setEditModeData,
      setEditableLocation,
      setError,
      setIsSubmit,
      updateLocation,
      vdpLink,
      validateForm,
      form,
    ]
  );

  // Define handleUpdate after handleUpdateClick
  const handleUpdate = useCallback((...args) => handleUpdateClick(...args), [handleUpdateClick]);

  // Effects
  useEffect(() => {
    if (editableLocation) {
      const settings = editableLocation?.settings || {};

      setAutoResponseEnabled(settings?.AUTO_RESPONSE_ENABLED ?? false);

      setLocation({
        ...editableLocation,
        enableMFA: settings?.MFA_ENABLED ?? false,
        mfaMethods: settings?.MFA_METHODS ?? [],
        phone: editableLocation.phone || "",
        latitude: settings?.LATITUDE ?? null,
        longitude: settings?.LONGITUDE ?? null,
        displayName: settings?.DISPLAY_NAME ?? null,
        locationLogo: settings?.LOCATION_LOGO ?? settings?.LOGO_URL ?? null,
        preferredLanguage: settings?.PREFERRED_LANGUAGE ?? null,
        timezone: editableLocation.timezone || null,
        country: editableLocation.country || null,
        state: editableLocation.state || null,
        postalCode: editableLocation.postalCode || "",
        replyToEmail: settings?.REPLY_TO_EMAIL || "",
        isPushToCRM: settings?.IS_PUSH_TO_CRM ?? false,
        isPushToDMS: settings?.IS_PUSH_TO_DMS ?? false,
        opportunityTypeId: editableLocation?.opportunityType?.id || null,
      });

      // Set logo if exists in settings
      if (settings?.LOCATION_LOGO || settings?.LOGO_URL) {
        setLogoList([
          {
            uid: "-1",
            name: "location-logo.png",
            status: "done",
            url: settings?.LOCATION_LOGO || settings?.LOGO_URL || "",
          },
        ]);
      }

      form.setFieldsValue({
        ...editableLocation,
        phone: editableLocation.phone || "",
        postalCode: editableLocation.postalCode || "",
        replyToEmail: settings?.REPLY_TO_EMAIL || "",
        latitude: settings?.LATITUDE ?? editableLocation.latitude,
        longitude: settings?.LONGITUDE ?? editableLocation.longitude,
        displayName: settings?.DISPLAY_NAME ?? editableLocation.displayName,
        preferredLanguage: settings?.PREFERRED_LANGUAGE ?? editableLocation.preferredLanguage,
        timezone: editableLocation.timezone || null,
        country: editableLocation.country || null,
        state: editableLocation.state || null,
        isPushToCRM: settings?.IS_PUSH_TO_CRM ?? false,
        isPushToDMS: settings?.IS_PUSH_TO_DMS ?? false,
        opportunityTypeId: editableLocation?.opportunityType?.id || null,
      });
      setvdpLink(editableLocation?.inventoryUrlFormat || "");
    } else {
      // Create mode: Set default language to English
      setLocation(prev => ({
        ...prev,
        preferredLanguage: "en",
      }));
      setLogoList([]);
      setvdpLink("");
      setAutoResponseEnabled(false);
      setError("");
      form.resetFields();
      form.setFieldsValue({
        preferredLanguage: "en",
      });
    }
  }, [editableLocation, form]);

  // This useEffect watches the isSubmit prop and calls handleUpdateClick() when isSubmit becomes true.
  // Its purpose is to allow a parent component to trigger the form submission programmatically by setting isSubmit to true.
  // If you only rely on the form's onFinish (i.e., user clicking the submit button), you do NOT need this useEffect.
  // If you want to support external/parent-triggered submit (e.g., a Save button outside the form), then this useEffect is needed.
  useEffect(() => {
    if (isSubmit) {
      handleUpdate();
    }
  }, [isSubmit, handleUpdate]);

  // Handlers
  const handleAddKeyword = keyword => {
    const ctl = document.getElementById("vdpLinkText");
    const startPos = ctl.selectionStart;
    const output = [vdpLink.slice(0, startPos), `${keyword}`, vdpLink.slice(startPos)].join("");
    setvdpLink(output.trim());
    if (vdpTextRef.current) vdpTextRef.current.focus();
  };

  // Upload logic
  const handleLogoChange = info => {
    const fileList = info.fileList.slice(-1);
    setLogoList(fileList);
  };
  const handleLogoRemove = () => {
    setLogoList([]);
  };
  const uploadButton = (
    <div className="flex flex-col items-center">
      <LuPlus />
      <div className="mt-2">Upload</div>
    </div>
  );

  // --- Render ---
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleUpdateClick}
      className="relative"
      // Always keep form fields in sync with location state
      initialValues={location || {}}
      onValuesChange={(changed, all) => setLocation(prev => ({ ...prev, ...all }))}
    >
      <Divider orientation="left" orientationMargin="0">
        {lang.contactDetails}
      </Divider>
      <Row gutter={16} className="!mb-3" align="middle">
        <Col span={3}>
          <FormLabel>{lang.logo}:</FormLabel>
        </Col>
        <Col span={7}>
          <UploadLogo
            logoList={logoList}
            onChange={handleLogoChange}
            onRemove={handleLogoRemove}
            uploadButton={uploadButton}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={<FormLabel>{lang.locationName}</FormLabel>}
            required
            validateStatus={!location?.title && error ? "error" : ""}
            help={!location?.title && error ? error : ""}
            hasFeedback
          >
            <TextInput
              placeholder={lang.locationName}
              value={location?.title}
              error={!location?.title && error}
              onChange={e => updateLocationForm("title", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={
              <span className="text-default text-[14px] font-normal">
                {lang.displayName}&nbsp;
                <Tooltip title={lang.displayNameTooltip}>
                  <LuCircleHelp />
                </Tooltip>
              </span>
            }
            help={!location?.displayName && error ? error : ""}
            hasFeedback
          >
            <TextInput
              placeholder={lang.displayName}
              value={location?.displayName}
              error={!location?.displayName && error}
              onChange={e => updateLocationForm("displayName", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.email}</FormLabel>}>
            <TextInput
              placeholder={lang.email}
              value={location?.email}
              onChange={e => updateLocationForm("email", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.phone}</FormLabel>}>
            <PhoneInput
              international
              withCountryCallingCode
              countryCallingCodeEditable={false}
              defaultCountry={DEFAULT_COUNTRY}
              placeholder={lang.phone}
              value={location?.phone}
              onChange={e => updateLocationForm("phone", e)}
              className="phoneInputCss"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.website}</FormLabel>}>
            <TextInput
              placeholder={lang.website}
              value={location?.website}
              onChange={e => updateLocationForm("website", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.preferredLanguage}</FormLabel>}>
            <LanguageSelect
              placeholder={lang.preferredLanguage}
              value={location?.preferredLanguage || undefined}
              onChange={e => updateLocationForm("preferredLanguage", e)}
              allowClear={!!editableLocation}
              languages={
                editableLocation
                  ? supportedLanguages.length > 0
                    ? supportedLanguages
                    : LANGUAGES.filter(lang => lang.value === "en")
                  : LANGUAGES.filter(lang => lang.value === "en")
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left" orientationMargin="0">
        {lang.addressDetails}
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={<FormLabel>{lang.region}</FormLabel>}
            required
            validateStatus={!location?.regionId && error ? "error" : ""}
            help={!location?.regionId && error ? error : ""}
          >
            <Select
              placeholder={lang.region}
              value={location?.regionId ? +location?.regionId : null}
              onChange={e => updateLocationForm("regionId", e)}
            >
              {regions?.map(item => (
                <Select.Option key={item?.id} value={+item?.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={<FormLabel>{lang.locationType}</FormLabel>}
            required
            validateStatus={!location?.locationType && error ? "error" : ""}
            help={!location?.locationType && error ? error : ""}
          >
            <Select
              placeholder={lang.locationType}
              value={location?.locationType}
              onChange={e => updateLocationForm("locationType", e)}
            >
              {LOCATION_TYPES.map(opt => (
                <Select.Option key={opt.key} value={opt.value}>
                  {lang[opt.key === "Corporate_Office" ? "corporateOffice" : opt.key.toLowerCase()]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.street}</FormLabel>}>
            <TextInput
              placeholder={lang.street}
              value={location?.addressLine1}
              onChange={e => updateLocationForm("addressLine1", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.city}</FormLabel>}>
            <TextInput
              placeholder={lang.city}
              value={location?.city}
              onChange={e => updateLocationForm("city", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.state}</FormLabel>}>
            <Select
              showSearch
              placeholder={lang.state}
              value={location?.state}
              onChange={e => updateLocationForm("state", e)}
            >
              {USStates.map((el, i) => (
                <Select.Option key={`key__${i}`} value={el.code}>
                  {el.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.postalCode}</FormLabel>}>
            <TextInput
              placeholder={lang.postalCode}
              value={location?.postalCode}
              onChange={e => updateLocationForm("postalCode", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.country}</FormLabel>}>
            <Select
              showSearch
              placeholder={lang.country}
              value={location?.country}
              onChange={e => updateLocationForm("country", e)}
            >
              {Countries.map((el, i) => (
                <Select.Option key={`key__${i}`} value={el.name}>
                  {el.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={<FormLabel>{lang.timeZone}</FormLabel>}
            required
            validateStatus={!location?.timezone && error ? "error" : ""}
            help={!location?.timezone && error ? error : ""}
          >
            <TimezoneSelect
              placeholder={lang.timeZone}
              value={location?.timezone}
              groupByRegion
              showPopularFirst
              onChange={e => updateLocationForm("timezone", e)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left" orientationMargin="0">
        {lang.geolocationDetails}
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.latitude}</FormLabel>}>
            <TextInput
              placeholder={lang.latitude}
              value={location?.latitude}
              onChange={e => updateLocationForm("latitude", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.longitude}</FormLabel>}>
            <TextInput
              placeholder={lang.longitude}
              value={location?.longitude}
              onChange={e => updateLocationForm("longitude", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label={<FormLabel>{lang.mapLink}</FormLabel>}>
            <TextInput
              placeholder={lang.mapLink}
              value={location?.geoLink}
              onChange={e => updateLocationForm("geoLink", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left" orientationMargin="0">
        {lang.otherDetails}
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<FormLabel>{lang.vdpLinkFormat}</FormLabel>}>
            <Input
              id="vdpLinkText"
              value={vdpLink}
              onChange={e => setvdpLink(e.target.value)}
              ref={vdpTextRef}
              placeholder={lang.vdpLinkFormat}
            />
            <CustomButton
              key="vinNumber"
              className="px-1"
              btnText="{vinNumber}"
              type="link"
              htmlType="button"
              handleClick={() => handleAddKeyword("{vinNumber}")}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={<FormLabel>{lang.replyToEmail}</FormLabel>}
            rules={[{ type: "email", message: lang.invalidEmail }]}
          >
            <Input
              placeholder={lang.replyToEmail}
              value={location?.replyToEmail}
              onChange={e => updateLocationForm("replyToEmail", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <p className="text-default text-sm font-normal mb-2">Default opportunity type</p>
        <SelectOpportunityStatus
          opportunityTypes={opportunityTypes}
          placeholder="Select opportunity type"
          loading={opportunityTypesLoading}
          onlyParentSelectable
          value={location?.opportunityTypeId}
          onChange={value => updateLocationForm("opportunityTypeId", value)}
        />
      </Row>
      <Divider orientation="left" orientationMargin="0">
        Location settings
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item>
            <Checkbox
              name="enableMFA"
              checked={location?.enableMFA || false}
              onChange={e => updateLocationForm("enableMFA", e.target.checked)}
            >
              {lang.enableMFA}
            </Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item>
            <Checkbox
              name="autoResponseEnabled"
              checked={autoResponseEnabled}
              onChange={e => setAutoResponseEnabled(e.target.checked)}
            >
              {lang.enableAutoResponse}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
      {location?.enableMFA && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={<FormLabel>{lang.mfaMethods}</FormLabel>}>
              <Checkbox
                checked={location?.mfaMethods?.includes(MFA_METHODS.SOFTWARE_TOKEN_MFA)}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(location?.mfaMethods || []), MFA_METHODS.SOFTWARE_TOKEN_MFA]
                    : location?.mfaMethods?.filter(
                        method => method !== MFA_METHODS.SOFTWARE_TOKEN_MFA
                      );
                  updateLocationForm("mfaMethods", updatedMethods);
                }}
              >
                {lang.authenticatorApp}
              </Checkbox>
              <Checkbox
                checked={location?.mfaMethods?.includes(MFA_METHODS.EMAIL_MFA)}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(location?.mfaMethods || []), MFA_METHODS.EMAIL_MFA]
                    : location?.mfaMethods?.filter(method => method !== MFA_METHODS.EMAIL_MFA);
                  updateLocationForm("mfaMethods", updatedMethods);
                }}
              >
                {lang.emailMFA}
              </Checkbox>
              <Checkbox
                checked={location?.mfaMethods?.includes(MFA_METHODS.SMS_MFA)}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(location?.mfaMethods || []), MFA_METHODS.SMS_MFA]
                    : location?.mfaMethods?.filter(method => method !== MFA_METHODS.SMS_MFA);
                  updateLocationForm("mfaMethods", updatedMethods);
                }}
              >
                {lang.smsMFA}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item>
            <Checkbox
              name="isPushToCRM"
              checked={location?.isPushToCRM || false}
              onChange={e => updateLocationForm("isPushToCRM", e.target.checked)}
            >
              Push leads to CRM
            </Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item>
            <Checkbox
              name="isPushToDMS"
              checked={location?.isPushToDMS || false}
              onChange={e => updateLocationForm("isPushToDMS", e.target.checked)}
            >
              Push leads to DMS
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default LocationForm;
